// ffmpeg.wasm engine wrapper — single-threaded core, lazy CDN load.
//
// We deliberately use the single-threaded @ffmpeg/core so the panel works
// WITHOUT cross-origin isolation (no COOP/COEP headers required on the dev
// server). The core (~30MB wasm) is fetched on first use via toBlobURL and
// cached by the browser. Nothing here ever touches the network except the
// one-time core download.

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import type { ResolutionPreset } from './types';

// Pin a known-good core version compatible with @ffmpeg/ffmpeg ^0.12.
const CORE_VERSION = '0.12.10';
const DEFAULT_BASE = `https://unpkg.com/@ffmpeg/core@${CORE_VERSION}/dist/umd`;
const CORE_BASE_URL =
  (typeof process !== 'undefined' && process.env?.REACT_APP_FFMPEG_CORE_URL) ||
  DEFAULT_BASE;

let singleton: FFmpeg | null = null;
let loadPromise: Promise<FFmpeg> | null = null;

export type LogSink = (line: string) => void;

/**
 * Lazily construct + load the ffmpeg engine. Idempotent: concurrent callers
 * share one load promise; later callers reuse the ready instance.
 */
export async function getEngine(onLog?: LogSink): Promise<FFmpeg> {
  if (singleton && (singleton as any).loaded) return singleton;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const ffmpeg = new FFmpeg();
    if (onLog) {
      ffmpeg.on('log', ({ message }) => onLog(message));
    }
    await ffmpeg.load({
      coreURL: await toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    singleton = ffmpeg;
    return ffmpeg;
  })();

  try {
    return await loadPromise;
  } catch (err) {
    loadPromise = null; // allow retry on failure
    throw err;
  }
}

/** Subscribe to ffmpeg's 0..1 progress events. Returns an unsubscribe fn. */
export function onEngineProgress(
  ffmpeg: FFmpeg,
  cb: (ratio: number) => void,
): () => void {
  const handler = ({ progress }: { progress: number }) => {
    // ffmpeg sometimes emits >1 or negative near boundaries — clamp.
    cb(Math.max(0, Math.min(1, progress)));
  };
  ffmpeg.on('progress', handler);
  return () => ffmpeg.off('progress', handler);
}

const vfChain = (p: ResolutionPreset) =>
  `scale=${p.width}:${p.height}:force_original_aspect_ratio=decrease,` +
  `pad=${p.width}:${p.height}:(ow-iw)/2:(oh-ih)/2,setsar=1,fps=${p.fps}`;

/**
 * Concatenate clips into a single normalized MP4.
 *
 * Strategy (robust, handles mixed resolutions / fps / codecs):
 *   1. PRIMARY — single-pass concat FILTER that scales+pads every clip to the
 *      chosen canvas and stitches video + audio. Preserves original audio.
 *   2. FALLBACK — if the primary pass fails (commonly because a clip has no
 *      audio stream, which breaks the audio concat), re-normalize each clip
 *      individually with a SILENT audio track, then concat-copy. Always yields
 *      a valid, downloadable file.
 *
 * Returns the encoded MP4 bytes. Caller owns blob/URL lifecycle.
 */
export async function spliceClips(
  ffmpeg: FFmpeg,
  files: File[],
  preset: ResolutionPreset,
  onPhase: (label: string) => void,
): Promise<Uint8Array> {
  if (files.length === 0) throw new Error('No clips to splice.');

  const inputs: string[] = [];
  onPhase('Loading clips…');
  for (let i = 0; i < files.length; i++) {
    const name = `in_${i}.src`;
    await ffmpeg.writeFile(name, await fetchFile(files[i]));
    inputs.push(name);
  }

  const out = 'out.mp4';
  const vf = vfChain(preset);

  try {
    onPhase('Splicing (with audio)…');
    const args: string[] = [];
    for (const f of inputs) args.push('-i', f);

    // Build filter_complex: normalize each stream, then concat.
    let fc = '';
    for (let i = 0; i < inputs.length; i++) {
      fc += `[${i}:v]${vf}[v${i}];`;
      fc += `[${i}:a]aresample=async=1:first_pts=0[a${i}];`;
    }
    for (let i = 0; i < inputs.length; i++) fc += `[v${i}][a${i}]`;
    fc += `concat=n=${inputs.length}:v=1:a=1[v][a]`;

    await ffmpeg.exec([
      ...args,
      '-filter_complex', fc,
      '-map', '[v]',
      '-map', '[a]',
      '-c:v', 'libx264',
      '-preset', 'veryfast',
      '-crf', '23',
      '-pix_fmt', 'yuv420p',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-ar', '44100',
      '-ac', '2',
      '-movflags', '+faststart',
      out,
    ]);
  } catch {
    // FALLBACK — per-clip normalize with silent audio, then concat-copy.
    onPhase('Splicing (normalizing audio)…');
    const normalized: string[] = [];
    for (let i = 0; i < inputs.length; i++) {
      onPhase(`Normalizing clip ${i + 1} of ${inputs.length}…`);
      const norm = `norm_${i}.mp4`;
      await ffmpeg.exec([
        '-i', inputs[i],
        '-f', 'lavfi',
        '-i', 'anullsrc=channel_layout=stereo:sample_rate=44100',
        '-filter_complex', `[0:v]${vf}[v]`,
        '-map', '[v]',
        '-map', '1:a',
        '-c:v', 'libx264',
        '-preset', 'veryfast',
        '-crf', '23',
        '-pix_fmt', 'yuv420p',
        '-c:a', 'aac',
        '-b:a', '128k',
        '-shortest',
        norm,
      ]);
      normalized.push(norm);
    }

    onPhase('Joining clips…');
    const listFile = 'concat_list.txt';
    const listBody = normalized.map((n) => `file '${n}'`).join('\n');
    await ffmpeg.writeFile(listFile, new TextEncoder().encode(listBody));
    await ffmpeg.exec([
      '-f', 'concat',
      '-safe', '0',
      '-i', listFile,
      '-c', 'copy',
      '-movflags', '+faststart',
      out,
    ]);

    // Cleanup intermediates.
    for (const n of normalized) {
      try { await ffmpeg.deleteFile(n); } catch { /* noop */ }
    }
    try { await ffmpeg.deleteFile(listFile); } catch { /* noop */ }
  }

  onPhase('Reading output…');
  const data = (await ffmpeg.readFile(out)) as Uint8Array;

  // Cleanup inputs + output from the virtual FS to free memory.
  for (const f of inputs) {
    try { await ffmpeg.deleteFile(f); } catch { /* noop */ }
  }
  try { await ffmpeg.deleteFile(out); } catch { /* noop */ }

  return data;
}
